// @flow
import * as React from 'react';
import {Transition} from 'react-transition-group';

export type Props = {
  in?: boolean,
  delay?: number,
  timeout?: number,
  children: React.Element<any>,
  scale?: number,
};

export function Fade(props: Props): React.Element<any> {
  const {children, in: inProp, delay, timeout, ...others} = props;
  const nodeRef = React.useRef(null);

  const defaultStyle = {
    transition: `opacity ${timeout ?? 0}ms ease-in-out`,
    transitionDelay: delay ?? 0,
  };

  const transitionStyles = {
    entered: {opacity: 1},
  };

  return (
    <Transition {...others} nodeRef={nodeRef} in={inProp} timeout={timeout ?? 0}>
      {(state, childProps) => {
        //console.log(state, (new Date()).getTime());
        return React.cloneElement(children, {
          style: {
            //visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            opacity: 0,
            ...defaultStyle,
            ...transitionStyles[state],
          },
          ...childProps,
        });
      }}
    </Transition>
  );
}

export type PropsSlide = Props & {
  direction?: string,
  defaultStyle?: Object,
  transitionStyles?: {entering?: Object, entered?: Object, exiting?: Object, exited?: Object},
};

function getTranslateValue(direction: string, node: React.ElementRef<any>, scale?: number) {
  const rect = node.getBoundingClientRect();
  const containerWindow = ((node && node.ownerDocument) || document).defaultView || window;
  let transform;

  if (node.fakeTransform) {
    transform = node.fakeTransform;
  } else {
    const computedStyle = containerWindow.getComputedStyle(node);
    transform =
      computedStyle.getPropertyValue('-webkit-transform') ||
      computedStyle.getPropertyValue('transform');
  }

  let offsetX = 0;
  let offsetY = 0;
  
  if (transform && transform !== 'none' && typeof transform === 'string') {
    const transformValues = transform.split('(')[1].split(')')[0].split(',');
    offsetX = parseInt(transformValues[4], 10);
    offsetY = parseInt(transformValues[5], 10);
  }
  const nodeScale = scale ?? 1;
  //console.log(containerWindow.innerWidth, offsetX, rect.left, rect.left / nodeScale)
  if (direction === 'left') {
    return `translateX(${containerWindow.innerWidth / nodeScale}px) translateX(${rect.left - offsetX}px)`;
  }

  if (direction === 'right') {
    return `translateX(-${(rect.left / nodeScale) + (rect.width / nodeScale) - offsetX}px)`;
  }

  if (direction === 'up') {
    return `translateY(${containerWindow.innerHeight}px) translateY(${offsetY - (rect.top / nodeScale)}px)`;
  }

  // direction === 'down'
  return `translateY(-${(rect.top / nodeScale) + (rect.height / nodeScale) - offsetY}px)`;
}

export function setTranslateValue(direction: string, node: React.ElementRef<any>, scale?: number) {
  const transform = getTranslateValue(direction, node, scale);

  if (transform) {
    node.style.webkitTransform = transform;
    node.style.transform = transform;
  }
}

export function Slide(props: PropsSlide): React.Element<any> {
  const {
    children,
    direction = 'down',
    in: inProp,
    delay,
    timeout,
    defaultStyle,
    transitionStyles = {},
    scale,
    ...others
  } = props;
  const childrenRef = React.useRef(null);

  const handleEnter = React.useCallback(
    (node, isAppearing) => {
      setTranslateValue(direction, childrenRef.current, scale);
    },
    [direction, scale]
  );

  const handleEntering = React.useCallback(
    (node, isAppearing) => {
      if (childrenRef.current) {
        childrenRef.current.style.transition = `all ${timeout ?? 0}ms ease-in-out ${delay ?? 0}ms`;
        childrenRef.current.style.transform = 'none';
      }
    },
    [delay, timeout]
  );

  const handleExit = React.useCallback(
    (node, isAppearing) => {
      if (childrenRef.current) {
        childrenRef.current.style.transition = `all ${timeout ?? 0}ms ease ${delay ?? 0}ms`;
      }
      setTranslateValue(direction, childrenRef.current, scale);
    },
    [direction, delay, timeout, scale]
  );

  const handleExited = React.useCallback((node, isAppearing) => {
    if (childrenRef.current) {
      childrenRef.current.style.webkitTransition = '';
      childrenRef.current.style.transition = '';
    }
  }, []);

  const updatePosition = React.useCallback(() => {
    if (childrenRef.current) {
      setTranslateValue(direction, childrenRef.current, scale);
    }
  }, [direction, scale]);

  React.useEffect(() => {
    if (!inProp) {
      // We need to update the position of the drawer when the direction change and
      // when it's hidden.
      updatePosition();
    }
  }, [inProp, scale, updatePosition]);

  return (
    <Transition
      {...others}
      nodeRef={childrenRef}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
      in={inProp}
      timeout={timeout ?? 0}>
      {(state, childProps) => {
        return React.cloneElement(children, {
          ref: childrenRef,
          style: {
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            ...defaultStyle,
            ...transitionStyles[state],
          },
          ...childProps,
        });
      }}
    </Transition>
  );
}
